import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AgentSelector } from "@/components/AgentSelector";
import { CommunicationInboxToolbar } from "@/components/communications/CommunicationInboxToolbar";
import type { CommunicationSortField } from "@/types/communications";
import type { SortDirection, ViewMode } from "@/hooks/use-view-preferences";
import {
  AlertCircle,
  Loader2,
  RefreshCw,
  Sparkles,
  Link2,
  Unlink,
} from "lucide-react";

export interface CommunicationFilterTab {
  id: string;
  label: string;
  count?: number;
}

interface CommunicationInboxLayoutProps {
  icon: ReactNode;
  title: string;
  description: string;
  connected: boolean;
  connectionLabel?: string;
  onConnect: () => void;
  onDisconnect: () => void;
  isConnecting?: boolean;
  isDisconnecting?: boolean;
  onRefresh: () => void;
  isRefreshing?: boolean;
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: string;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  sortField: CommunicationSortField;
  sortDirection: SortDirection;
  onSortChange: (field: CommunicationSortField, direction: SortDirection) => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  filterTabs?: CommunicationFilterTab[];
  activeFilterTab?: string;
  onFilterTabChange?: (tabId: string) => void;
  selectedAgentId: string | null;
  onAgentChange: (agentId: string | null) => void;
  selectedCount: number;
  onAnalyzeSelected: () => void;
  isAnalyzing?: boolean;
  batchProgress?: { completed: number; total: number; currentLabel?: string };
  analyzeLabel?: string;
  requireAgentForAnalyze?: boolean;
  integrationToolbar?: ReactNode;
  cardTitle?: string;
  cardDescription?: string;
  children: ReactNode;
  disabled?: boolean;
  /** Filter agent dropdown to agents scoped for this integration (gmail, fireflies, zoom, etc.). */
  integrationScope?: string;
  /** Hide the default agent selector + analyze row (e.g. when integrationToolbar provides its own). */
  hideAgentBar?: boolean;
  /** Show refresh button in page header. Set false when refresh is on the filter toolbar. */
  showHeaderRefresh?: boolean;
  /** Extra controls rendered inline in the filter toolbar (e.g. date picker). */
  toolbarEndSlot?: ReactNode;
}

export function CommunicationInboxLayout({
  icon,
  title,
  description,
  connected,
  connectionLabel,
  onConnect,
  onDisconnect,
  isConnecting = false,
  isDisconnecting = false,
  onRefresh,
  isRefreshing = false,
  isLoading = false,
  isError = false,
  errorMessage,
  searchQuery,
  onSearchChange,
  searchPlaceholder,
  sortField,
  sortDirection,
  onSortChange,
  viewMode,
  onViewModeChange,
  filterTabs,
  activeFilterTab,
  onFilterTabChange,
  selectedAgentId,
  onAgentChange,
  selectedCount,
  onAnalyzeSelected,
  isAnalyzing = false,
  batchProgress,
  analyzeLabel = "Analyze selected",
  requireAgentForAnalyze = true,
  integrationToolbar,
  cardTitle = "Inbox",
  cardDescription = "Browse and analyze messages with your AI agents.",
  children,
  disabled = false,
  integrationScope,
  hideAgentBar = false,
  showHeaderRefresh = true,
  toolbarEndSlot,
}: CommunicationInboxLayoutProps) {
  const isBusy = disabled || isAnalyzing;

  return (
    <div className="container max-w-8xl py-8 space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            {icon}
            <h1 className="text-2xl font-semibold">{title}</h1>
            {connected && (
              <Badge variant="secondary" className="text-xs">
                Connected{connectionLabel ? `: ${connectionLabel}` : ""}
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground mt-1">{description}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {connected ? (
            <>
              {showHeaderRefresh && (
                <Button
                  variant="outline"
                  onClick={onRefresh}
                  disabled={isRefreshing || isBusy}
                >
                  {isRefreshing ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Refresh
                </Button>
              )}
              <Button
                variant="outline"
                onClick={onDisconnect}
                disabled={isDisconnecting || isBusy}
              >
                {isDisconnecting ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Unlink className="h-4 w-4 mr-2" />
                )}
                Disconnect
              </Button>
            </>
          ) : (
            <Button onClick={onConnect} disabled={isConnecting}>
              {isConnecting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Link2 className="h-4 w-4 mr-2" />
              )}
              Connect {title.split(" ")[0]}
            </Button>
          )}
        </div>
      </div>

      {!connected && (
        <div className="rounded-lg border border-dashed bg-muted/30 px-4 py-6 text-center space-y-3">
          <p className="text-sm text-muted-foreground">
            Connect your account to view and analyze messages with AI agents.
          </p>
          <Button onClick={onConnect} disabled={isConnecting}>
            {isConnecting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Link2 className="h-4 w-4 mr-2" />
            )}
            Connect account
          </Button>
        </div>
      )}

      {connected && isAnalyzing && batchProgress && (
        <div className="rounded-lg border bg-muted/30 px-4 py-3 flex items-center gap-3 text-sm">
          <Loader2 className="h-4 w-4 animate-spin shrink-0 text-primary" />
          <div className="min-w-0 flex-1">
            <p className="font-medium">
              Analyzing {batchProgress.completed + 1} of {batchProgress.total}
            </p>
            {batchProgress.currentLabel && (
              <p className="text-muted-foreground truncate">{batchProgress.currentLabel}</p>
            )}
          </div>
        </div>
      )}

      {connected && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{cardTitle}</CardTitle>
            <CardDescription>{cardDescription}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {filterTabs && filterTabs.length > 0 && onFilterTabChange && (
              <Tabs value={activeFilterTab} onValueChange={onFilterTabChange}>
                <TabsList>
                  {filterTabs.map((tab) => (
                    <TabsTrigger key={tab.id} value={tab.id} disabled={isBusy}>
                      {tab.label}
                      {tab.count != null && (
                        <span className="ml-1.5 text-xs text-muted-foreground">({tab.count})</span>
                      )}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            )}

            <CommunicationInboxToolbar
              searchQuery={searchQuery}
              onSearchChange={onSearchChange}
              searchPlaceholder={searchPlaceholder}
              sortField={sortField}
              sortDirection={sortDirection}
              onSortChange={onSortChange}
              viewMode={viewMode}
              onViewModeChange={onViewModeChange}
              disabled={isBusy}
              endSlot={toolbarEndSlot}
              onRefresh={!showHeaderRefresh ? onRefresh : undefined}
              isRefreshing={!showHeaderRefresh ? isRefreshing : undefined}
            />

            {!hideAgentBar && (
              <div className="flex flex-wrap items-center gap-3 rounded-md border bg-muted/20 px-3 py-2">
                <AgentSelector
                  value={selectedAgentId}
                  onValueChange={onAgentChange}
                  compact
                  integrationScope={integrationScope}
                />
                <Button
                  size="sm"
                  onClick={onAnalyzeSelected}
                  disabled={isBusy || selectedCount === 0 || (requireAgentForAnalyze && !selectedAgentId)}
                >
                  {isAnalyzing ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Sparkles className="h-4 w-4 mr-2" />
                  )}
                  {analyzeLabel} ({selectedCount})
                </Button>
              </div>
            )}

            {integrationToolbar}

            {isError && (
              <div className="flex items-center gap-2 text-destructive text-sm">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{errorMessage || "Failed to load messages"}</span>
              </div>
            )}

            {isLoading ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin mr-2" />
                Loading...
              </div>
            ) : (
              children
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
